import type { Sim, RunState } from './sim';
import type { PlayerState, World } from './world';

/**
 * SNAPSHOT / RESTORE
 * Serializes the complete simulation state (world pools, players, run state,
 * RNG streams) into plain data. Used for: save-and-resume mid-run, replay
 * verification, and the future host→client state sync (docs/MULTIPLAYER.md).
 *
 * Typed arrays are copied by reference into the snapshot object; call
 * `structuredClone(snapshot)` (or postMessage) if you need an independent copy.
 */
export interface SimSnapshot {
  version: 1;
  tick: number;
  time: number;
  arenaRadius: number;
  rng: number[];
  lootRng: number[];
  players: PlayerState[];
  /** Per-player arrays mirror RunState; sets/maps are flattened for structured-clone/JSON. */
  run: Omit<RunState, 'offers' | 'banished' | 'upgrades'> & { offers: string[][]; banished: string[][]; upgrades: [string, number][][] };
  boss: { id: string; slot: number } | null;
  kills: number;
  bossKills: number;
  lingerDefs: World['lingerDefs'];
  pools: Record<string, Record<string, ArrayBufferView | number>>;
}

type PoolLike = { pool: { alive: Uint8Array; free: Int32Array; freeCount: number; count: number; high: number } } & Record<string, unknown>;

function dumpPool(p: PoolLike): Record<string, ArrayBufferView | number> {
  const out: Record<string, ArrayBufferView | number> = {};
  for (const [k, v] of Object.entries(p)) {
    if (k === 'pool') continue;
    if (ArrayBuffer.isView(v)) out[k] = (v as unknown as { slice(): ArrayBufferView }).slice();
  }
  out.__alive = p.pool.alive.slice();
  out.__free = p.pool.free.slice();
  out.__freeCount = p.pool.freeCount;
  out.__count = p.pool.count;
  out.__high = p.pool.high;
  return out;
}

function loadPool(p: PoolLike, d: Record<string, ArrayBufferView | number>): void {
  for (const [k, v] of Object.entries(d)) {
    if (k.startsWith('__')) continue;
    const target = p[k];
    if (ArrayBuffer.isView(target) && ArrayBuffer.isView(v)) (target as unknown as { set(a: ArrayBufferView): void }).set(v as never);
  }
  p.pool.alive.set(d.__alive as Uint8Array);
  p.pool.free.set(d.__free as Int32Array);
  p.pool.freeCount = d.__freeCount as number;
  p.pool.count = d.__count as number;
  p.pool.high = d.__high as number;
}

export function snapshot(sim: Sim): SimSnapshot {
  const w = sim.world;
  const r = sim.run;
  return {
    version: 1,
    tick: w.tick,
    time: w.time,
    arenaRadius: w.arenaRadius,
    rng: sim.rng.getState(),
    lootRng: sim.lootRng.getState(),
    players: w.players.map((p) => ({ ...p, pets: [...p.pets] })),
    run: {
      ...r,
      offers: r.offers.map((list) => list.map((o) => o.id)),
      banished: r.banished.map((set) => [...set]),
      upgrades: r.upgrades.map((m) => [...m.entries()]),
      portals: r.portals.map((p) => ({ ...p })),
      portalIdle: [...r.portalIdle],
    },
    boss: sim.ctx.boss ? { id: sim.ctx.boss.def.id, slot: sim.ctx.boss.slot } : null,
    kills: sim.ctx.kills,
    bossKills: sim.ctx.bossKills,
    lingerDefs: w.lingerDefs.map((l) => ({ ...l })),
    pools: {
      projectiles: dumpPool(w.projectiles as unknown as PoolLike),
      enemies: dumpPool(w.enemies as unknown as PoolLike),
      pickups: dumpPool(w.pickups as unknown as PoolLike),
      hazards: dumpPool(w.hazards as unknown as PoolLike),
      pets: dumpPool(w.pets as unknown as PoolLike),
    },
  };
}

/** Restore into an existing Sim built with the same RunConfig + registry. */
export function restore(sim: Sim, s: SimSnapshot): void {
  const w = sim.world;
  w.tick = s.tick;
  w.time = s.time;
  w.arenaRadius = s.arenaRadius;
  w.grid.resize(s.arenaRadius);
  sim.rng.setState(s.rng);
  sim.lootRng.setState(s.lootRng);
  s.players.forEach((p, i) => Object.assign(w.players[i], p, { pets: [...p.pets] }));
  const r = sim.run;
  Object.assign(r, s.run, {
    offers: s.run.offers.map((ids) => ids.map((id) => sim.reg.upgrades.get(id)!).filter(Boolean)),
    banished: s.run.banished.map((ids) => new Set(ids)),
    upgrades: s.run.upgrades.map((entries) => new Map(entries)),
    portals: (s.run.portals ?? []).map((p) => ({ ...p })),
    portalIdle: s.run.portalIdle ? [...s.run.portalIdle] : r.portalIdle.map(() => 0),
    portalLeader: s.run.portalLeader ?? -1,
    portalCountdown: s.run.portalCountdown ?? 0,
    afterPicks: s.run.afterPicks ?? 'playing',
    advanceSerial: s.run.advanceSerial ?? 0,
    lastAdvanceEra: s.run.lastAdvanceEra ?? false,
    introT: s.run.introT ?? 0,
    objectiveClears: s.run.objectiveClears ?? 0,
    perfectDefenses: s.run.perfectDefenses ?? 0,
    fastHunts: s.run.fastHunts ?? 0,
    flawlessEscorts: s.run.flawlessEscorts ?? 0,
    noHitBosses: s.run.noHitBosses ?? 0,
  });
  sim.ctx.kills = s.kills;
  sim.ctx.bossKills = s.bossKills;
  // Reset the key map too: restoring into a sim that already interned defs
  // (online co-op resync) must rebuild the table at the snapshot's exact
  // indices, or live projectiles' `linger` indices point past the array.
  w.resetLingers();
  for (const l of s.lingerDefs) w.internLinger(l);
  loadPool(w.projectiles as unknown as PoolLike, s.pools.projectiles);
  loadPool(w.enemies as unknown as PoolLike, s.pools.enemies);
  loadPool(w.pickups as unknown as PoolLike, s.pools.pickups);
  loadPool(w.hazards as unknown as PoolLike, s.pools.hazards);
  if (s.pools.pets) loadPool(w.pets as unknown as PoolLike, s.pools.pets);
  const bossDef = s.boss ? sim.reg.bosses.get(s.boss.id) : undefined;
  sim.ctx.boss = bossDef && s.boss ? { def: bossDef, slot: s.boss.slot } : null;
  sim.reloadStage(r.eraIndex, r.stageIndex);
  // Snapshots created before objective-wave membership was serialized must be
  // conservative: keep every live enemy as part of the current wave rather
  // than ending a resumed wave stage immediately.
  if (!Object.prototype.hasOwnProperty.call(s.pools.enemies, 'objectiveWave') && sim.currentStage.objective?.type === 'waves') {
    for (let i = 1; i < w.enemies.pool.high; i++) {
      if (w.enemies.pool.alive[i] && !w.enemies.isBoss[i]) w.enemies.objectiveWave[i] = 1;
    }
  }
  sim.recomputeStats();
}

const TYPED_ARRAYS: Record<string, new (buffer: ArrayBuffer) => ArrayBufferView> = {
  Int8Array, Uint8Array, Uint8ClampedArray, Int16Array, Uint16Array,
  Int32Array, Uint32Array, Float32Array, Float64Array,
};

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(binary);
}

function base64ToBytes(value: string): Uint8Array {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/** Compact JSON transport for localStorage; typed arrays retain their exact type and bytes. */
export function serializeSnapshot(value: SimSnapshot): string {
  return JSON.stringify(value, (_key, item) => {
    if (!ArrayBuffer.isView(item)) return item;
    const bytes = new Uint8Array(item.buffer, item.byteOffset, item.byteLength);
    return { __typedArray: item.constructor.name, data: bytesToBase64(bytes) };
  });
}

export function deserializeSnapshot(value: string): SimSnapshot {
  return JSON.parse(value, (_key, item) => {
    if (!item || typeof item !== 'object' || typeof item.__typedArray !== 'string' || typeof item.data !== 'string') return item;
    const Type = TYPED_ARRAYS[item.__typedArray];
    if (!Type) throw new Error(`Unsupported typed array ${item.__typedArray}`);
    const bytes = base64ToBytes(item.data);
    return new Type(bytes.buffer);
  }) as SimSnapshot;
}
